import React, { useState } from 'react';
import styled from '@emotion/styled';
import { useSwipeable } from 'react-swipeable';
import { CartItem, LocalItem } from '~Types';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useCurrency } from '~Hooks/useCurrency';
import { useCart } from '~Components/CartProvider';
import { useStorage } from '~Components/Storage';
import { isServiceChargeItem } from '~Utils/serviceCharge';
import { useI18n } from '~Hooks';

const Item = styled.div<{
  selected?: boolean;
}>`
  flex-flow: column nowrap;
  box-sizing: border-box;
  display: flex;
  padding-left: 8px;
  padding-right: 16px;
  border-bottom: 1px solid var(--colorBlueGray1200);
  border-left: ${({ selected }) =>
    '4px solid ' + (selected ? 'var(--colorUIYellow)' : 'transparent')};
  min-height: 40px;
`;

const InnerWrapper = styled.div`
  padding: 12px 0;
`;

const ItemHeader = styled.div`
  display: flex;
  align-items: stretch;
  color: var(--colorWhite);
  align-items: flex-start;
`;

const ItemBody = styled.div`
  display: flex;
  flex-flow: column nowrap;
  padding-bottom: 8px;
  margin-left: 40px;
  color: var(--colorWhite);
  align-items: flex-start;
`;

const ContentGroup = styled.div`
  display: flex;
  margin-right: 4px;
  align-items: center;
`;

const LeftContent = styled(ContentGroup)`
  flex: 1;
  margin-right: 8px;

  & > h5:first-of-type {
    text-align: center;
  }
`;
const RightContent = styled(ContentGroup)`
  display: inline-flex;
`;

const Type = styled.div`
  background: ${(props) => props.color};
  padding: 4px;
  text-align: center;
  height: 24px;
  min-width: 24px;
  color: var(--colorBlueGray1200);
  line-height: 16px;
  border-radius: 24px;
  font-weight: 600;
  font-size: 10px;
  box-sizing: border-box;
  margin-right: 8px;
`;

const Strike = styled.span`
  color: var(--colorBlueGray600);
  text-decoration: line-through;
`;

const Price = styled.h5`
  font-size: 12px;
  box-sizing: border-box;
  text-align: right;
  margin: 0;
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  color: var(--colorWhite);
  gap: 6px;
`;

const DiscountName = styled.span`
  color: var(--colorUIOrange);
  font-size: 10px;
  text-align: right;
  display: block;
  margin-bottom: 2px;
  font-weight: bold;
`;

const PriceWrapper = styled.div`
  max-width: 92px;
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  margin-right: 0;
`;

const Qty = styled(Heading)`
  margin-right: 8px;
  min-width: 32px;
`;

export interface Props {
  item: {
    entry: {
      amount: number;
      discount: number;
      fraction?: number;
      discountTitle?: string;
      offerId?: string;
    };
  } & LocalItem;
  changeCourseType: (id: string) => void;
  hidePrice?: boolean;
}

const ItemRow = ({ item, changeCourseType, hidePrice }: Props) => {
  const { i18n } = useI18n();
  const cart = useCart();

  const [isScrolling, setIsScrolling] = useState(false);
  const originalPrice = item.entry.amount;
  const discount = item.entry.discount;
  const fraction = item.entry.fraction ?? 1;
  const currency = useCurrency();
  const [storage] = useStorage();
  const count = !storage.compressedCart && fraction ? fraction : item.count ? item.count : 1;
  const hasVariations =
    item.variations.length > 0 || item.removedVariations.length > 0 || item.message;

  const handlers = useSwipeable({
    delta: 10,
    onSwiping: ({ dir }) => {
      // Don't handle scroll if not necessary (unscrollable or side swipes)
      if (dir === 'Left' || dir === 'Right') {
        return;
      }
      setIsScrolling(true);
    },
  });

  let timeout: ReturnType<typeof setTimeout>;
  const onTouchEnd = () => {
    timeout = setTimeout(() => {
      setIsScrolling(false);
    }, 100);
  };

  const selectedVariations = item.variations.map((variation) => (
    <Paragraph key={variation.id} level={4} color="var(--colorWhite)" bottom={4}>
      {variation.name}
    </Paragraph>
  ));

  const removedVariations = item.removedVariations.map((variation) => (
    <Paragraph key={variation.id} level={4} color="var(--colorUIRed)" bottom={4}>
      {variation.name}
    </Paragraph>
  ));

  const formattedItem: LocalItem = {
    ...item,
    amount: item.entry.amount,
    discount: item.entry.discount,
    discountTitle: item.entry.discountTitle,
    fraction: item.entry.fraction,
  };

  return (
    <Item
      {...handlers}
      onTouchEnd={onTouchEnd}
      key={item.id}
      selected={cart.selectedItems.has(item.id)}>
      <InnerWrapper>
        <ItemHeader>
          <LeftContent
            onTouchEnd={() =>
              !isScrolling && cart.toggleSelectedItem(formattedItem, storage.compressedCart)
            }>
            <Qty level={5} color="var(--colorWhite)" right={8}>
              {count}
            </Qty>
            <Heading level={5} color="var(--colorWhite)">
              {item.title}
            </Heading>
          </LeftContent>
          <RightContent>
            {item.courseType && (
              <Type onTouchStart={() => changeCourseType(item.id)} color={item.courseType.color}>
                {item.courseType.label}
              </Type>
            )}
            <PriceWrapper>
              {formattedItem.discount && formattedItem.discount > 0 && !item.entry.offerId ? (
                <DiscountName>
                  {formattedItem.discountTitle || i18n('Discount.Manual')}
                </DiscountName>
              ) : null}

              <Price>
                {originalPrice !== item.entry.amount - discount && (
                  <Strike>{currency.format(originalPrice)}</Strike>
                )}
                {item.itemId && isServiceChargeItem(item.title)
                  ? '-'
                  : `${currency.format(item.entry.amount - discount)}`}
              </Price>
            </PriceWrapper>
          </RightContent>
        </ItemHeader>
        {hasVariations && (
          <ItemBody
            onTouchStart={() => cart.toggleSelectedItem(formattedItem, storage.compressedCart)}>
            {selectedVariations}
            {removedVariations}
            {item.message && (
              <Paragraph level={4} color="var(--colorUISand)" bottom={4}>
                {item.message}
              </Paragraph>
            )}
          </ItemBody>
        )}
      </InnerWrapper>
    </Item>
  );
};

export default React.memo(ItemRow);
